import { memo, useCallback, useMemo } from 'react';
import classnames from 'classnames/bind';
import { Label, SearchMultiInput } from '@wildberries/ui-kit';
import { DatePicker } from '@wildberries/lazy-calendar';
import i18next from 'i18next';
import { OnDatePickedChangeParamsType } from '@wildberries/lazy-calendar/lib/_types';
import { SearchMultiInputOption } from '@wildberries/ui-kit/lib/search-multi-input/_types';
import { DATEPICKER_TRANSLATIONS_CONFIG } from '@/pages/supplies-management/_constants';
import { WAREHOUSES_LIMITS_PAGE_TRANSLATIONS } from '@/pages/supplies-management/children/warehouses-limits/_constants/translations';
import { COMMON_TRANSLATIONS } from '@/pages/supplies-management/_constants/translations';
import { PreLoader } from '@/pages/supplies-management/_components/pre-loader';
import {
  TConvertedCoefficientsTypeReport,
  TSearchMultiInputOptionsArray,
  TWarehousesLimitsStorage,
} from '@/pages/supplies-management/children/warehouses-limits/redux/warehouses-limits-module/types';
import { AlertError } from '@/pages/supplies-management/_components/alert-error';
import { ChooseWarehouse } from './_components/choose-warehouse';
import { LimitsTable } from './_components/limits-table';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Warehouses-limits-view';

type TProps = {
  onSelectWarehouse: (optionClickEvent: Array<SearchMultiInputOption>) => void;
  onChangeDate: (date: Date) => void;
  selectedWarehouse: TSearchMultiInputOptionsArray | null;
  areWarehousesLoading: boolean;
  selectedDate: Date | null;
  areLimitsLoading: boolean;
  coefficientsReportData: TConvertedCoefficientsTypeReport | null;
  onSearchChange: (value: string) => void;
  filteredWarehouses: Array<SearchMultiInputOption>;
  banText: string;
  allowedDates: TWarehousesLimitsStorage['allowedDates'];
  costForPalette: number;
};

export const WarehousesLimitsView = memo(
  ({
    onSelectWarehouse,
    selectedWarehouse,
    onChangeDate,
    areWarehousesLoading,
    selectedDate,
    areLimitsLoading,
    onSearchChange,
    filteredWarehouses,
    coefficientsReportData,
    banText,
    allowedDates,
    costForPalette,
  }: TProps) => {
    const handleSetDate = useCallback(
      ({ startDate }: OnDatePickedChangeParamsType) => {
        onChangeDate(startDate);
      },
      [onChangeDate],
    );
    const isChooseDateDisabled = useMemo(
      () => !selectedWarehouse || !selectedWarehouse.length,
      [selectedWarehouse],
    );

    const startDate = useMemo(() => selectedDate || new Date(), [selectedDate]);

    const hasLimits = useMemo(
      () => selectedWarehouse?.length,
      [selectedWarehouse],
    );

    return (
      <div className={cn(BLOCK_NAME)}>
        {Boolean(banText) && (
          <AlertError
            text={banText}
            title={i18next.t(COMMON_TRANSLATIONS.banModalTitle)}
          />
        )}

        <div className={cn(`${BLOCK_NAME}__filters-container`)}>
          <div className={cn(`${BLOCK_NAME}__select-container`)}>
            <div className={cn(`${BLOCK_NAME}__label-container`)}>
              <Label htmlFor="warehouses-multi-input">
                {i18next.t(COMMON_TRANSLATIONS.chooseWarehouse)}
              </Label>
            </div>

            <SearchMultiInput
              id="warehouses-multi-input"
              isDisabled={areWarehousesLoading}
              isLoading={areWarehousesLoading}
              name="warehouses-multi-input"
              onChange={onSelectWarehouse}
              onChangeSearchText={onSearchChange}
              options={filteredWarehouses}
              optionsPlaceholder={i18next.t(
                WAREHOUSES_LIMITS_PAGE_TRANSLATIONS.warehouseNotFound,
              )}
              value={selectedWarehouse || []}
            />
          </div>

          <div className={cn(`${BLOCK_NAME}__calendar-container`)}>
            <DatePicker
              allowedDates={allowedDates}
              dateDownLimit={new Date()}
              disabled={isChooseDateDisabled}
              id="unplanned-supply-date-picker"
              name="unplanned-supply-date-picker"
              onChange={handleSetDate}
              startDate={startDate}
              translationConfig={{
                ...DATEPICKER_TRANSLATIONS_CONFIG,
                mainInputLabel: i18next.t(
                  WAREHOUSES_LIMITS_PAGE_TRANSLATIONS.startingFromDate,
                ),
              }}
            />
            <PreLoader isLoading={areLimitsLoading} />
          </div>
        </div>

        <div className={cn(`${BLOCK_NAME}__table-container`)}>
          {hasLimits ? (
            <LimitsTable
              coefficientsReportData={coefficientsReportData}
              costForPalette={costForPalette}
              selectedDate={selectedDate}
              selectedWarehouse={selectedWarehouse}
            />
          ) : (
            <ChooseWarehouse />
          )}
        </div>
      </div>
    );
  },
);
